import "./styles.css";
import "./writer.css";
import { api, setCsrf, Me } from "./api";
import { initTheme } from "./theme";
import { mountReader } from "./reader";
import { mountWriter } from "./writer";

const app = document.getElementById("app") as HTMLElement;
let me: Me = { authenticated: false, is_admin: false };

// Silent SSO: an anonymous visit asks the auth server once, without any UI,
// whether this browser already has a session ("look for the token"). If it
// does, we come back signed in and — for the author — land straight in the
// writer. If it doesn't, the auth server bounces us back with ?sso=none and
// the reader renders as usual. No login button anywhere.
const PROBE_KEY = "book.sso.probedAt";
const PROBE_COOLDOWN_MS = 10 * 60 * 1000;

function probedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(PROBE_KEY) || 0);
    return Date.now() - at < PROBE_COOLDOWN_MS;
  } catch {
    // Storage blocked (private mode, strict settings) — the ?sso=none marker
    // below is the fallback guard against a redirect loop.
    return false;
  }
}

function markProbed() {
  try {
    sessionStorage.setItem(PROBE_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/// Remove the ?sso=none marker from the address bar; returns whether it was there.
function takeSsoMarker(): boolean {
  const params = new URLSearchParams(location.search);
  if (params.get("sso") !== "none") return false;
  params.delete("sso");
  const qs = params.toString();
  history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : "") + location.hash);
  return true;
}

async function boot() {
  initTheme();

  const ssoFailed = takeSsoMarker();

  try {
    me = await api.me();
    if (me.csrf) setCsrf(me.csrf);
  } catch {
    /* unauthenticated reader */
  }

  if (!me.authenticated && !ssoFailed && !probedRecently()) {
    markProbed();
    const back = location.pathname + location.search + location.hash;
    location.replace(`/auth/login?prompt=none&return_to=${encodeURIComponent(back)}`);
    return;
  }

  window.addEventListener("hashchange", route);
  route();
}

async function route() {
  const hash = location.hash || "#/";

  // Explicit reader route, used by the writer's "Preview" link so the author
  // can read the book without being bounced back into the editor.
  const wantsReader = hash.startsWith("#/read") || hash.startsWith("#/book/");

  // The author's home is the writer: a valid admin session renders the admin
  // app immediately, with no intermediate screen.
  if (hash.startsWith("#/admin") || (me.is_admin && !wantsReader)) {
    if (!me.authenticated) {
      location.href = `/auth/login?return_to=${encodeURIComponent("/#/admin")}`;
      return;
    }
    if (me.is_admin) {
      await mountWriter(app, me);
      return;
    }
    // Signed in, but this identity isn't the author — offer the account switch
    // rather than a dead end, then fall through to the reader.
    app.innerHTML = notAuthorState(me);
    const sw = document.getElementById("switchAccount");
    if (sw) sw.onclick = () => (location.href = "/login?switch=1");
    return;
  }

  // Reader. Optional explicit slug: #/book/<slug>
  let slug: string | null = null;
  const m = hash.match(/^#\/book\/([^/]+)/);
  if (m) slug = decodeURIComponent(m[1]);

  if (!slug) {
    let books = await api.getBookList().catch(() => []);
    if (books.length === 0) {
      app.innerHTML = emptyState(me);
      const start = document.getElementById("startWriting");
      if (start) start.onclick = () => (location.hash = "#/admin");
      return;
    }
    slug = books[0].slug;
  }
  await mountReader(app, slug, me);
}

function emptyState(me: Me): string {
  return `
    <div class="center-msg">
      <div>
        <div style="font-size:42px;margin-bottom:10px">📖</div>
        <div>No book has been published yet.</div>
        ${
          me.is_admin
            ? `<button class="btn primary" id="startWriting" style="margin-top:16px">Start writing</button>`
            : `<div style="margin-top:10px;font-size:14px;color:var(--ink-faint)">Check back soon.</div>`
        }
      </div>
    </div>`;
}

function notAuthorState(me: Me): string {
  const who = me.email || me.name || "this account";
  return `
    <div class="center-msg">
      <div>
        <div>Signed in as ${who}, which isn't the author account.</div>
        <button class="btn" id="switchAccount" style="margin-top:16px">Use another account</button>
        <div style="margin-top:10px"><a href="#/read">Continue reading</a></div>
      </div>
    </div>`;
}

boot();
